import { Injectable, signal } from '@angular/core';

import { Storage } from '@ionic/storage-angular';
import { ELingua, INaarGoTripRow } from './backend/naar-api-client';
import { Network } from '@capacitor/network';
import { Device, OperatingSystem } from '@capacitor/device';
import { LangCode, LangCodeToELingua } from '../types/languages.type';
import { Capacitor } from '@capacitor/core';
import { TranslationObject } from '@ngx-translate/core';
import { ThemePreference } from '../types/theme.type';

type StorageKey = LangCode | 'lastOpenedTripId' | 'onboardingComplete' | 'themePreference' | number;

/**
 * Versione dello schema dei viaggi in cache.
 * Va incrementata ogni volta che il payload di `trip/get` guadagna un campo usato dalla UI:
 * i viaggi già in IndexedDB vengono così riscaricati una sola volta, al primo accesso utile.
 * 1 = introduzione della versione (payload con `passengers`, issue #23)
 */
export const TRIP_CACHE_SCHEMA_VERSION = 1;

/** Viaggio in cache, marcato con la versione di schema con cui è stato salvato. */
type CachedTrip = INaarGoTripRow & { _schemaVersion?: number };

@Injectable({
	providedIn: 'root',
})
export class StorageService {

	private storage: Storage | null = null;

	private localTrips: Map<number, CachedTrip> = null;

	//? https://capacitorjs.com/docs/apis/network
	public isNetworkConnected$ = signal<boolean>(false);

	public operatingSystem: OperatingSystem = null;
	public isMobile = false;

	public onboardingComplete = false;

	constructor(
		private ionicStorage: Storage,
	) {
		Network.addListener('networkStatusChange', status => {
			this.isNetworkConnected$.set(status.connected);
		});
	}

	//#region Storage wrappers
	private async get<T>(key: StorageKey): Promise<T | null> {
		return await this.storage.get(key.toString()) as T | null;
	}

	private async set(key: StorageKey, value: any): Promise<void> {
		await this.storage.set(key.toString(), value);
		return;
	}

	private async remove(key: StorageKey): Promise<void> {
		await this.storage.remove(key.toString());
		return;
	}
	//#endregion Storage wrappers

	public async init() {
		const status = await Network.getStatus();
		this.isNetworkConnected$.set(status.connected);

		this.storage = await this.ionicStorage.create();
		const keys = await this.storage.keys();
		const ids = keys.map(key => parseInt(key)).filter(key => !isNaN(key));
		const trips = await Promise.all(
			ids.map(async id => {
				return await this.get<CachedTrip>(id);
			})
		);
		this.localTrips = new Map(trips.map(trip => [trip.id, trip]));

		const info = await Device.getInfo();
		this.operatingSystem = info.operatingSystem;
		this.isMobile = this.operatingSystem === 'ios' || this.operatingSystem === 'android';

		this.onboardingComplete = await this.get<boolean>('onboardingComplete') ?? false;
	}

	public async completeOnboarding() {
		await this.set('onboardingComplete', true);
		this.onboardingComplete = true;
	}

	//#region Theme preference
	public async getThemePreference(): Promise<ThemePreference | null> {
		return await this.get<ThemePreference>('themePreference');
	}
	public async setThemePreference(pref: ThemePreference): Promise<void> {
		await this.set('themePreference', pref);
	}
	//#endregion Theme preference

	//#region Translations
	public async getTranslation(langCode: LangCode): Promise<TranslationObject> {
		return await this.get<TranslationObject>(langCode);
	}
	public async setTranslation(langCode: LangCode, translation: TranslationObject) {
		await this.set(langCode, translation);
	}
	//#endregion Translations

	//#region Last opened trip
	/**
	 * Returns the last opened trip id.
	 * If invalid, returns the first trip id in the local storage or null if no trips are available.
	 */
	public async getLastOpenedTripId(): Promise<number | null> {
		const lastOpenedTripId = await this.get<number>('lastOpenedTripId');
		return lastOpenedTripId && this.hasTrip(lastOpenedTripId) ? lastOpenedTripId : this.getTripsIds()[0] || null;
	}
	public async setLastOpenedTripId(id: number): Promise<void> {
		await this.set('lastOpenedTripId', id);
	}
	//#endregion Last opened trip

	//#region Trips
	public hasTrip(id: number): boolean {
		return this.localTrips.has(id);
	}
	public getTrip(id: number): INaarGoTripRow | null {
		return this.localTrips.get(id) || null;
	}

	/** Returns the list of trips ids stored in the local storage. */
	public getTripsIds(): number[] {
		return Array.from(this.localTrips.keys());
	}

	/** Returns the list of trips stored in the local storage. */
	public getTrips(): INaarGoTripRow[] {
		return Array.from(this.localTrips.values());
	}

	/** True quando il viaggio in cache è stato salvato con uno schema più vecchio di quello corrente. */
	public isTripCacheStale(id: number): boolean {
		const trip = this.localTrips.get(id);
		return !!trip && (trip._schemaVersion ?? 0) < TRIP_CACHE_SCHEMA_VERSION;
	}

	/** Update or add a trip in the local storage */
	public async setTrip(trip: INaarGoTripRow): Promise<void> {
		//* Il payload arriva da http.post come oggetto piatto: lo spread è lossless
		const cached: CachedTrip = { ...trip, _schemaVersion: TRIP_CACHE_SCHEMA_VERSION };
		this.localTrips.set(cached.id, cached);
		await this.set(cached.id, cached);
	}

	public async removeTrip(tripId: number): Promise<void> {
		this.localTrips.delete(tripId);
		await this.remove(tripId);
	}
	//#endregion Trips

	//#region Device info
	public async getSystemLanguage(): Promise<ELingua> {
		const lang = await Device.getLanguageCode();
		return LangCodeToELingua(lang.value);
	}

	public isCameraAvailable(): boolean {
		//? https://capacitorjs.com/docs/apis/camera
		//? Maybe add a check/request for the permission
		return Capacitor.isPluginAvailable('Camera');
	}
	//#endregion Device info
}
